import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { EXIT_CHECKPOINT_RADIUS, EXIT_CHECKPOINT_Z } from '../../data/tunnel';
import { computeIsMobile } from '../../core/useIsMobile';

const CYAN = '#00ffff';
const CYAN_CORE = '#e8ffff';

function createStripeTexture() {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#05080c';
  ctx.fillRect(0, 0, 512, 128);

  ctx.save();
  ctx.translate(256, 64);
  ctx.rotate(-0.72);
  const w = 18;
  const gap = 34;
  for (let x = -420; x < 420; x += gap) {
    const g = ctx.createLinearGradient(x, 0, x + w, 0);
    g.addColorStop(0, '#00c8e6');
    g.addColorStop(0.45, '#00ffff');
    g.addColorStop(0.55, '#ffffff');
    g.addColorStop(1, '#00d4f0');
    ctx.fillStyle = g;
    ctx.fillRect(x, -220, w, 440);
  }
  ctx.restore();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(14, 2);
  tex.anisotropy = 8;
  return tex;
}

function createGlowTexture() {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const ctx = c.getContext('2d')!;
  const g = ctx.createRadialGradient(128, 128, 8, 128, 128, 128);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.18, 'rgba(0,255,255,0.7)');
  g.addColorStop(0.45, 'rgba(0,220,255,0.22)');
  g.addColorStop(1, 'rgba(0,180,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function createSparkTexture() {
  const c = document.createElement('canvas');
  c.width = 32;
  c.height = 32;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(10, 10, 12, 12);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

type Spark = {
  x: number;
  y: number;
  z: number;
  s: number;
  v: number;
};

export function ExitCheckpoint({ runnerZ }: { runnerZ: number }) {
  const stripeMap = useMemo(() => createStripeTexture(), []);
  const glowMap = useMemo(() => createGlowTexture(), []);
  const sparkMap = useMemo(() => createSparkTexture(), []);
  const body = useRef<THREE.Group>(null);
  const loader = useRef<THREE.Group>(null);
  const count = computeIsMobile() ? 22 : 40;
  const sparks = useMemo<Spark[]>(
    () =>
      Array.from({ length: count }, () => ({
        x: (Math.random() - 0.5) * 1.0,
        y: Math.random() * 1.6,
        z: (Math.random() - 0.5) * 1.0,
        s: 0.016 + Math.random() * 0.022,
        v: 0.35 + Math.random() * 0.5,
      })),
    [count],
  );
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const inst = useRef<THREE.InstancedMesh>(null);

  const near = THREE.MathUtils.clamp(
    1 - Math.abs(runnerZ - EXIT_CHECKPOINT_Z) / 5,
    0,
    1,
  );
  const onIt = Math.abs(runnerZ - EXIT_CHECKPOINT_Z) < EXIT_CHECKPOINT_RADIUS;

  const ringR = EXIT_CHECKPOINT_RADIUS * 0.78;
  const tube = 0.038;

  useFrame((_, dt) => {
    if (body.current) body.current.rotation.y += dt * 1.15;
    if (loader.current) loader.current.rotation.y -= dt * 2.35;
    if (stripeMap) stripeMap.offset.x = (stripeMap.offset.x + dt * 0.35) % 1;

    if (!inst.current) return;
    const r = ringR * 0.92;
    for (let i = 0; i < sparks.length; i++) {
      const p = sparks[i];
      p.y += p.v * dt;
      if (p.y > 1.85) {
        p.y = 0.08;
        const ang = Math.random() * Math.PI * 2;
        const rad = Math.random() * r;
        p.x = Math.cos(ang) * rad;
        p.z = Math.sin(ang) * rad;
      }
      dummy.position.set(p.x, p.y, p.z);
      dummy.scale.setScalar(p.s * (0.55 + 1 - p.y / 1.85));
      dummy.rotation.y = p.y * 2;
      dummy.updateMatrix();
      inst.current.setMatrixAt(i, dummy.matrix);
    }
    inst.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={[0, tube + 0.02, EXIT_CHECKPOINT_Z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -tube, 0]}>
        <circleGeometry args={[ringR * 1.7, 48]} />
        <meshBasicMaterial
          map={glowMap}
          color={CYAN}
          transparent
          opacity={0.42 + near * 0.22}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      <group ref={body}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
          <torusGeometry args={[ringR, tube, 14, 72]} />
          <meshStandardMaterial
            map={stripeMap}
            color={CYAN}
            emissive={CYAN}
            emissiveIntensity={0.85}
            roughness={0.28}
            metalness={0.45}
            toneMapped={false}
          />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[ringR, tube * 0.42, 10, 64]} />
          <meshBasicMaterial
            color={CYAN_CORE}
            transparent
            opacity={0.45}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      </group>

      <group ref={loader}>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[ringR, tube * 1.15, 12, 48, Math.PI * 0.72]} />
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={0.95}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      </group>

      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[ringR * 0.62, tube * 0.45, 10, 48]} />
        <meshBasicMaterial
          color={CYAN}
          transparent
          opacity={0.55}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      <instancedMesh ref={inst} args={[undefined, undefined, count]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={sparkMap}
          color={CYAN_CORE}
          transparent
          opacity={0.9}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </instancedMesh>

      <pointLight
        position={[0, 0.22, 0]}
        intensity={onIt ? 3.2 : 1.5 + near * 1.2}
        distance={5.2}
        decay={2}
        color={CYAN}
      />
    </group>
  );
}
